import React, { useEffect, useState, memo, useRef } from 'react';
import moment from 'moment';
import { Formik } from 'formik';
import { useParams } from 'react-router-dom';
import { isEmpty, forEach, isEqual, difference } from 'lodash';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import Form from 'components/ui/Form';
import {
  ModalContent,
  FormSectionTitle,
  FormWrapper,
  GridContainer,
} from 'styles/form';
import {
  createClinic,
  deleteClinic,
  showClinic,
  updateClinic,
  getClinicSlots,
  toggleClinicStaff,
} from 'api/clinic';
import { showOrganization } from 'api/organization';
import { getScheduler } from 'api/scheduler';
import { getAppointments, getAppointmentsByClinicId } from 'api/appointment';

import { formatAppointmentToCalendar } from 'helpers/format-appointment-to-calendar';

import Calendar from 'components/pages/Clinics/Clinic/Calendar/';
import { useSnackBar } from 'context/SnackBarContext';
import { getService } from 'api/service';
import {
  siteNameGenerator,
  subLocationNameGenerator,
} from 'helpers/site-name-generator';
import Client from './Client';
import Time from './Time';
import Service from './Service';
import Staffing from './Staffing';
import Scheduler from './Scheduler';
import { initialValues, validationSchema } from '../clinic.config';
import Header from './Header';
import {
  ModalContentWrapper,
  LeftContainer,
  RightContainer,
  StyledModal,
  Content,
  SchedulerSection,
} from './styles';

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const Clinic = memo(
  ({ modalOpen, handleModalClose, blockRouteChange, setBlockRouteChange }) => {
    const { id } = useParams();

    const loaded = useRef(false);
    const [clinic, setClinic] = useState({});

    const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

    const {
      id: clinicId,
      start_time: clinicStartTime,
      end_time: clinicEndTime,
      start_date: clinicStartDate,
      end_date: clinicEndDate,
      multi_days: clinicMultiDays,
      status: clinicStatus,
      service_duration: clinicServiceDuration,
      estimated_participants: clinicEstimatedParticipants,
      booked_slots_count: bookedParticipants,
      organization,
      sublocation,
      service,
      schedule,
      staff,
      times,
      concurrent_slots: clinicConcurrentSlots,
    } = clinic || {};
    const siteName = siteNameGenerator(organization, sublocation);
    const { link, slots: schedulerSlots } = schedule || {};

    const [moreMenuOpen, setMoreMenuOpen] = useState(false);
    const [date, setDate] = useState(new Date());
    const [statusTag, setStatusTag] = useState(clinicStatus);
    const [selectedStaff, setSelectedStaff] = useState([]);
    const [staffList, setStaffList] = useState([]);
    const [clinicHoursPerOccurrence, setClinicHoursPerOccurrence] = useState(0);
    const [clinicHours, setClinicHours] = useState(0);
    const [clinicMinsPerOccurrence, setClinicMinsPerOccurrence] = useState(0);
    const [occurrence, setOccurrence] = useState(0);
    const [clinicMins, setClinicMins] = useState(0);

    /* --------------- Appointments --------------- */

    const [selectedDate, setSelectedDate] = useState(
      moment(clinicStartDate).startOf('day').toDate(),
    );
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [appointments, setAppointments] = useState([]);

    const fetchAppointments = async () => {
      setLoadingAppointments(true);

      const { data } = await getAppointmentsByClinicId(selectedDate, clinic.id);
      setLoadingAppointments(false);

      setAppointments(formatAppointmentToCalendar(data.data));
    };

    useEffect(async () => {
      if (!isEmpty(clinic)) {
        await fetchAppointments();
      }
    }, [clinic, selectedDate]);

    useEffect(() => {
      if (staff) {
        setStaffList(staff);
        setSelectedStaff(staff.map(({ id }) => id));
      }
    }, [staff]);

    if (typeof window !== 'undefined' && !loaded.current) {
      if (!document.querySelector('#google-maps')) {
        loadScript(
          `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_DISTANCE_MATRIX_API_KEY}`,
          document.querySelector('head'),
          'google-maps',
        );
      }

      loaded.current = true;
    }

    useEffect(async () => {
      if (id) {
        const { data } = await showClinic(id, true);

        setClinic(data.data);
      }
    }, []);

    useEffect(() => {
      if (clinicStatus) {
        setStatusTag(clinicStatus);
      }
    }, [clinicStatus]);

    const handleDelete = async () => {
      try {
        await deleteClinic(id);
        setMessage('Clinic deleted');
        setSeverity('success');
      } catch (error) {
        setMessage('Something went wrong. Failed to delete clinic.');
        setSeverity('error');
      }

      setOpenSnackBar(true);
      handleModalClose(true);
    };

    const handleSave = async (values) => {
      const clinicStaff = staff && staff.map(({ id }) => id);
      const staffToUnassign = difference(clinicStaff, selectedStaff);
      const staffToAssign = difference(selectedStaff, clinicStaff);

      if (!isEmpty(clinic)) {
        try {
          await updateClinic(id, values, clinic, statusTag);
          setMessage('Saved changes');
          setSeverity('success');
        } catch (error) {
          console.error(error);
          setMessage('Something went wrong. Failed to save changes.');
          setSeverity('error');

          setOpenSnackBar(true);
          handleModalClose(true);
          return;
        }

        if (!isEqual(clinicStaff, selectedStaff)) {
          if (!isEmpty(staffToAssign)) {
            await toggleClinicStaff(clinicId, staffToAssign, 1);
          }

          if (!isEmpty(staffToUnassign)) {
            await toggleClinicStaff(clinicId, staffToUnassign, 0);
          }
        }
      } else {
        try {
          await createClinic(values);
          setMessage('Clinic created');
          setSeverity('success');
        } catch (error) {
          console.error(error);
          setMessage('Something went wrong. Failed to create clinic.');
          setSeverity('error');
        }
      }

      setOpenSnackBar(true);
      handleModalClose(true);
    };

    const toggleMoreMenu = () => {
      setMoreMenuOpen(!moreMenuOpen);
    };

    const copySchedulerLink = () => {
      navigator.clipboard.writeText(link);
    };

    const {
      id: orgId,
      name: orgName,
      address: orgAddress,
    } = organization || {};

    const formRef = useRef();

    const allowBeforeToday = id && clinicStatus === 'Draft';

    return (
      <MuiPickersUtilsProvider utils={DateFnsUtils}>
        <Formik
          initialValues={initialValues(clinic)}
          validationSchema={validationSchema(allowBeforeToday)}
          enableReinitialize={id}
          innerRef={formRef}
          onSubmit={handleSave}
        >
          {({
            values,
            errors,
            handleChange,
            submitForm,
            setFieldValue,
            touched,
          }) => {
            const {
              organizationId,
              subLocationId,
              startDate,
              startTime,
              endDate,
              endTime,
              bookingCutOffDaysNum,
              serviceDuration,
              actualParticipants,
              estimatedParticipants,
              serviceId,
              schedulerId,
              multiDays,
              concurrentSlots,
            } = values;

            const [sublocationOptions, setSublocationOptions] = useState([]);
            const [scheduler, setScheduler] = useState({});
            useState();

            const clinicNursingHours = concurrentSlots * clinicHours;

            const initValues = initialValues(clinic);

            const unsavedChange = !isEqual(
              {
                ...values,
                startTime: moment(startTime).format('MM-DD-YYYY'),
                endTime: moment(endTime).format('MM-DD-YYYY'),
              },
              {
                ...initValues,
                startTime: moment(initValues.startTime).format('MM-DD-YYYY'),
                endTime: moment(initValues.endTime).format('MM-DD-YYYY'),
              },
            );

            if (unsavedChange) {
              setBlockRouteChange(true);
            } else {
              setBlockRouteChange(false);
            }

            useEffect(() => {
              const start = moment(startTime, 'HH:mm:ss');
              const end = moment(endTime, 'HH:mm:ss');
              const difference = moment.duration(end.diff(start));
              const timeDifferenceHours =
                difference < 0
                  ? difference.asHours() + 24
                  : difference.asHours();
              const timeDifferenceMins =
                difference < 0
                  ? difference.asMinutes() + 24 * 60
                  : difference.asMinutes();

              const dateDifference = moment
                .duration(moment(endDate).diff(moment(startDate)))
                .asDays();

              if (timeDifferenceHours < 0) {
                setClinicMinsPerOccurrence(timeDifferenceMins + 24 * 60);
                setClinicHoursPerOccurrence(timeDifferenceHours + 24);
                setOccurrence(dateDifference);
              } else {
                setClinicMinsPerOccurrence(timeDifferenceMins);
                setClinicHoursPerOccurrence(timeDifferenceHours);
                setOccurrence(dateDifference + 1);
              }

              setClinicHours(occurrence * clinicHoursPerOccurrence);
              setClinicMins(occurrence * clinicMinsPerOccurrence);
            }, [
              startTime,
              endTime,
              endDate,
              startDate,
              clinicHoursPerOccurrence,
              clinicMinsPerOccurrence,
              occurrence,
            ]);

            useEffect(async () => {
              if (typeof organizationId === 'number') {
                const { data } = await showOrganization(organizationId);
                const subLocationData = data.data.sublocations;

                const formattedSublocationOptions = subLocationData.map(
                  (subLocation) => ({
                    ...subLocation,
                    formatted_name: subLocationNameGenerator(subLocation),
                  }),
                );

                setSublocationOptions(formattedSublocationOptions);
              }

              if (!organizationId && subLocationId) {
                setFieldValue('subLocationId', undefined);
              }
            }, [organizationId]);

            useEffect(async () => {
              if (typeof schedulerId === 'number') {
                const { data } = await getScheduler(schedulerId);

                setScheduler(data.data);
              }
            }, [schedulerId]);

            const recommendedNurses = Math.ceil(
              estimatedParticipants / (clinicMins / serviceDuration),
            );

            const recommendedTotalSlots =
              recommendedNurses * (clinicMins / serviceDuration);
            const timeSlots = concurrentSlots * (clinicMins / serviceDuration);

            return (
              <StyledModal
                open={modalOpen}
                handleClose={handleModalClose}
                submitForm={submitForm}
                typeText="Clinic"
                maxWidth="lg"
                handleDelete={handleDelete}
                blockRouteChange={blockRouteChange}
              >
                <Content>
                  <ModalContentWrapper>
                    <ModalContent>
                      <LeftContainer>
                        {id && (
                          <Header
                            orgName={orgName}
                            orgAddress={orgAddress}
                            statusTag={statusTag}
                            setStatusTag={setStatusTag}
                            toggleMoreMenu={toggleMoreMenu}
                            copySchedulerLink={copySchedulerLink}
                            link={link}
                            moreMenuOpen={moreMenuOpen}
                            bookedParticipants={bookedParticipants}
                            hours={clinicHours}
                            clinic={clinic}
                            nursingHours={clinicNursingHours}
                            siteName={siteName}
                            selectedStaff={selectedStaff}
                            times={times}
                            appointments={appointments}
                          />
                        )}

                        <FormWrapper>
                          <Form fullWidth>
                            <FormSectionTitle>Client</FormSectionTitle>
                            <GridContainer>
                              <Client
                                setFieldValue={setFieldValue}
                                organizationId={organizationId}
                                subLocationId={subLocationId}
                                organization={organization}
                                sublocation={sublocation}
                                sublocationOptions={sublocationOptions}
                                errors={errors}
                                touched={touched}
                              />
                            </GridContainer>

                            <FormSectionTitle>Time</FormSectionTitle>
                            <GridContainer>
                              <Time
                                setFieldValue={setFieldValue}
                                startDate={startDate}
                                startTime={startTime}
                                endDate={endDate}
                                endTime={endTime}
                                bookingCutOffDaysNum={bookingCutOffDaysNum}
                                handleChange={handleChange}
                                multiDays={multiDays}
                                errors={errors}
                                touched={touched}
                                initValues={initValues}
                                setSelectedStaff={setSelectedStaff}
                                setStaffList={setStaffList}
                              />
                            </GridContainer>

                            <FormSectionTitle>Service</FormSectionTitle>
                            <GridContainer>
                              <Service
                                errors={errors}
                                service={service}
                                serviceDuration={serviceDuration}
                                estimatedParticipants={estimatedParticipants}
                                actualParticipants={actualParticipants}
                                touched={touched}
                                handleChange={handleChange}
                                setFieldValue={setFieldValue}
                                serviceId={serviceId}
                                clinicServiceDuration={clinicServiceDuration}
                              />
                            </GridContainer>

                            <FormSectionTitle>Scheduler</FormSectionTitle>
                            <GridContainer>
                              <Scheduler
                                errors={errors}
                                touched={touched}
                                handleChange={handleChange}
                                scheduler={schedule || scheduler}
                                schedulerId={schedulerId}
                                setFieldValue={setFieldValue}
                                concurrentSlots={concurrentSlots}
                                recommendedNurses={recommendedNurses}
                                orgId={orgId}
                                organizationId={organizationId}
                                recommendedTotalSlots={recommendedTotalSlots}
                                timeSlots={timeSlots}
                              />
                            </GridContainer>

                            {id && (
                              <SchedulerSection>
                                <FormSectionTitle>Staffing</FormSectionTitle>
                                <Staffing
                                  clinicStaff={staff}
                                  sublocation={sublocation}
                                  schedule={schedule}
                                  selectedStaff={selectedStaff}
                                  setSelectedStaff={setSelectedStaff}
                                  staffList={staffList}
                                  setStaffList={setStaffList}
                                  serviceDuration={serviceDuration}
                                  schedulerSlots={schedulerSlots}
                                  estimatedParticipants={estimatedParticipants}
                                  clinicHours={clinicHours}
                                  recommendedNurses={recommendedNurses}
                                  startTime={startTime}
                                  endTime={endTime}
                                  endDate={endDate}
                                  startDate={startDate}
                                />
                              </SchedulerSection>
                            )}
                          </Form>
                        </FormWrapper>
                      </LeftContainer>

                      {id && (
                        <RightContainer>
                          <Calendar
                            clinicStartDate={clinicStartDate}
                            clinicEndDate={clinicEndDate}
                            clinicMultiDays={clinicMultiDays}
                            clinicStatus={clinicStatus}
                            clinic={clinic}
                            slots={times}
                            setDate={setDate}
                            concurrentSlots={clinicConcurrentSlots}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            loadingAppointments={loadingAppointments}
                            appointments={appointments}
                            fetchAppointments={fetchAppointments}
                          />
                        </RightContainer>
                      )}
                    </ModalContent>
                  </ModalContentWrapper>
                </Content>
              </StyledModal>
            );
          }}
        </Formik>
      </MuiPickersUtilsProvider>
    );
  },
);

export default Clinic;
